if (!customElements.get("product-library")) {
  customElements.define("product-library", class ProductLibrary extends HTMLElement {
    constructor() {
      super();
      this.handleTabClick = this.handleTabClick.bind(this);
      this.onIntersect    = this.onIntersect.bind(this);
      this.updateActiveByVisibility = this.updateActiveByVisibility.bind(this);
      this.scrollToTarget = this.scrollToTarget.bind(this);
      this.refreshOffset = this.refreshOffset.bind(this);
      this.handleInternalLinkClick = this.handleInternalLinkClick.bind(this);
      this.handleHashChange = this.handleHashChange.bind(this);
      this._rafPending = false;
      this._scrollCorrectionTimer = null;
      this.onResize = this.onResize.bind(this);

      this.querySelectorAll(".product-library--header a.link").forEach(tab =>
        tab.addEventListener("click", this.handleTabClick)
      );

      this.headerSelector = "h2[id], h3[id], .chapter[id]";
      this.headers = Array.from(this.querySelectorAll(this.headerSelector));
      // Add click listeners to headers so clicking a title scrolls to its chapter
      this.headers.forEach(header => {
        header.style.cursor = header.style.cursor || 'pointer';
        header.addEventListener('click', (evt) => {
          // If the click target is a link inside the header, let it behave normally
          if (evt.target && (evt.target.closest && evt.target.closest('a'))) return;
          const id = header.id;
          if (!id) return;
          // reuse existing behavior to find target and scroll
          const target = this.getTargetElement(id);
          if (target) {
            this.refreshOffset();
            this.scrollToTarget(target, { behavior: "auto" });
          }
          this.setActiveLink(id);
        });
      });
      this.navSelector = ".library_nav";
      this.refreshOffset();
      this.addEventListener("click", this.handleInternalLinkClick);
      window.addEventListener("hashchange", this.handleHashChange, { passive: true });
      if (window.location.hash) {
        requestAnimationFrame(this.handleHashChange);
      }

      const observerOptions = {
        rootMargin: "-50px 0px -60%",
        threshold: [0, 0.1, 0.25, 0.5, 1]
      };
      this.observer = new IntersectionObserver(this.onIntersect, observerOptions);
      this.headers.forEach(h2 => this.observer.observe(h2));

      window.addEventListener("scroll", () => {
        if (this._rafPending) return;
        this._rafPending = true;
        requestAnimationFrame(() => {
          this.updateActiveByVisibility();
          this._rafPending = false;
        });
      }, { passive: true });
      window.addEventListener("resize", this.onResize, { passive: true });

      this.updateActiveByVisibility();
    }

    handleTabClick(evt) {
      evt.preventDefault();
      const id = evt.currentTarget.hash.slice(1);
      const target = this.getTargetElement(id);
      if (target) {
        this.refreshOffset();
        this.scrollToTarget(target);
      }
      this.setActiveLink(id);
    }

    onIntersect(/* entries */) {
      this.updateActiveByVisibility();
    }

    updateActiveByVisibility() {
      if (!this.headers || this.headers.length === 0) return;

      const viewportTop = 0;
      const viewportBottom = window.innerHeight;

      let best = { id: null, visible: 0 };
      for (const h of this.headers) {
        const rect = h.getBoundingClientRect();
        const visibleHeight = Math.max(0, Math.min(rect.bottom, viewportBottom) - Math.max(rect.top, viewportTop));
        if (visibleHeight > best.visible) {
          best = { id: h.id, visible: visibleHeight };
        }
      }

      if (best.id) {
        this.setActiveLink(best.id);
      }
    }

    setActiveLink(id) {
      const nav = this.querySelector(this.navSelector) || document.querySelector(this.navSelector);
      if (!nav) return;

      const escapedId = this.escapeSelector(id);
      const newLink = nav.querySelector(`a[href="#${escapedId}"]`);
      const current = nav.querySelector("a.active");

      if (newLink && newLink !== current) {
        if (current) current.classList.remove("active");
        newLink.classList.add("active");
      }
    }

    onResize() {
      this.refreshOffset();
    }

    handleInternalLinkClick(evt) {
      if (evt.defaultPrevented) return;

      const anchor = evt.target && evt.target.closest && evt.target.closest('a[href^="#"]');
      if (!anchor) return;

      const { hash } = anchor;
      if (!hash || hash === "#") return;

      const id = decodeURIComponent(hash.slice(1));
      const target = this.getTargetElement(id);
      if (!target) return;

      evt.preventDefault();
      this.refreshOffset();
      const isNavLink = anchor.closest(this.navSelector);
      const behavior = anchor.dataset.scrollBehavior || (isNavLink ? "smooth" : "auto");
      this.scrollToTarget(target, { behavior });
      this.setActiveLink(id);
    }

    handleHashChange() {
      const { hash } = window.location;
      if (!hash || hash === "#") return;
      const id = decodeURIComponent(hash.slice(1));
      const target = this.getTargetElement(id);
      if (!target) return;

      this.refreshOffset();
      this.scrollToTarget(target, { behavior: "auto" });
      this.setActiveLink(id);
    }

    refreshOffset() {
      this.offset = this.calculateOffset();
      this.applyScrollMargin();
    }

    calculateOffset() {
      const stickyHeader = document.querySelector("sticky-header");
      const headerHeight = stickyHeader ? stickyHeader.getBoundingClientRect().height : 0;
      const extraSpacing = 34;
      this.offset = Math.max(0, Math.round(headerHeight + extraSpacing));
      return this.offset;
    }

    applyScrollMargin() {
      if (!this.headers || this.headers.length === 0) return;
      const margin = `${this.offset}px`;
      this.headers.forEach(header => {
        header.style.scrollMarginTop = margin;
      });
    }

    scrollToTarget(target, options = {}) {
      if (!target) return;
      const behavior = options.behavior || "smooth";
      const offset = this.offset || 0;
      const targetRect = target.getBoundingClientRect();
      const absoluteTop = window.pageYOffset + targetRect.top;
      const desiredTop = Math.max(0, Math.round(absoluteTop - offset));

      if (this._scrollCorrectionTimer) {
        clearTimeout(this._scrollCorrectionTimer);
        this._scrollCorrectionTimer = null;
      }

      if (behavior === "auto" || behavior === "instant") {
        window.scrollTo({ top: desiredTop, behavior: "auto" });
        return;
      }

      window.scrollTo({ top: desiredTop, behavior });

      this._scrollCorrectionTimer = window.setTimeout(() => {
        this.refreshOffset();
        const updatedOffset = this.offset || 0;
        const rect = target.getBoundingClientRect();
        const residual = rect.top - updatedOffset;
        if (Math.abs(residual) > 2) {
          const correctionTop = Math.max(0, Math.round(window.pageYOffset + residual));
          window.scrollTo({ top: correctionTop, behavior: "auto" });
        }
        this._scrollCorrectionTimer = null;
      }, 450);
    }

    getTargetElement(id) {
      if (!id) return null;
      const escapedId = this.escapeSelector(id);
      let node = this.querySelector(`h2#${escapedId}, h3#${escapedId}, div.chapter#${escapedId}`);
      if (!node) {
        node = document.getElementById(id);
      }
      if (!node) return null;
      const candidate = node.closest("div.chapter, h2[id], h3[id]") || node;
      if (this.contains(candidate)) return candidate;
      if (this.contains(node)) return node;
      return null;
    }

    escapeSelector(value) {
      if (!value) return "";
      if (typeof CSS !== "undefined" && CSS.escape) {
        return CSS.escape(value);
      }
      return value.replace(/([\0-\x1F\x7F\s!"#$%&'()*+,./:;<=>?@\[\\\]^`{|}~])/g, "\\$1");
    }
  });
}
